"use client";

import { usePathname, useRouter } from "next/navigation";
import {
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useMemo,
  useState,
} from "react";
import { ErrorBoundary } from "@/components/error-boundary";
import { BriefsCompactSection } from "./_components/assembly/briefs-compact-section";
import { BriefsCompact5Section } from "./_components/assembly/briefs-compact5-section";
import { BriefsFlatSection } from "./_components/assembly/briefs-flat-section";
import { BriefsSidebarSection } from "./_components/assembly/briefs-sidebar-section";
import { BottomNav } from "./_components/bottom-nav";
import { BriefsSection } from "./_components/briefs-section";
import { ExploreFilterSheet } from "./_components/explore-filter-sheet";
import { ExploreFooter } from "./_components/explore-footer";
import { ExploreSidebar } from "./_components/explore-sidebar";
import { FeaturedStoryCard } from "./_components/featured-story-card";
import { HeroSection } from "./_components/hero-section";
import { LayoutSwitcher } from "./_components/layout-variants/layout-switcher";
import { MobileInlineNewsletter } from "./_components/mobile-inline-newsletter";
import { MobileStoryTextCard } from "./_components/mobile-story-text-card";
import { useNavigationTransition } from "./_components/navigation-transition-provider";
import { PromptsSection } from "./_components/prompts-section";
import { SkillsSpotlightSection } from "./_components/skills-spotlight-section";
import { StickyCategoryPills } from "./_components/sticky-category-pills";
import { StoryGridCard } from "./_components/story-grid-card";
import { TodayStoriesSection } from "./_components/today-stories-section";
import { TopStoriesSection } from "./_components/top-stories-section";
import { useBrowserSearchParams } from "./_components/use-browser-search-params";
import { useTagToggle } from "./_components/use-tag-toggle";
import { WorkflowsSection } from "./_components/workflows-section";
import { useExploreRoute } from "./_lib/explore-route-context";
import { parseTagSlugs } from "./_lib/explore-search";
import { applyStoryFilters, type SelectedStoryFilters } from "./story-filters";
import type { ExploreCategorySlug, ExplorePageData } from "./types";

// ── Lazy-loaded F-series layout variants ───────────────────────
const ExploreF1Flagship = lazy(() =>
  import("./_components/layout-variants/explore-f1-flagship").then((m) => ({
    default: m.ExploreF1Flagship,
  }))
);
const ExploreF2Compact = lazy(() =>
  import("./_components/layout-variants/explore-f2-compact").then((m) => ({
    default: m.ExploreF2Compact,
  }))
);
const ExploreF3Editorial = lazy(() =>
  import("./_components/layout-variants/explore-f3-editorial").then((m) => ({
    default: m.ExploreF3Editorial,
  }))
);

export type BriefsVariant =
  | "default"
  | "compact"
  | "compact5"
  | "flat"
  | "sidebar";

const allowExploreLayoutMocks = process.env.NODE_ENV !== "production";

interface ExplorePageProps {
  activeCategory?: ExploreCategorySlug;
  briefsVariant?: BriefsVariant;
  categoryToolsRail?: ReactNode;
  data: ExplorePageData;
  initialSearch?: string;
}

function omitFilterGroup({
  filters,
  groupKey,
}: {
  filters: SelectedStoryFilters;
  groupKey: string;
}): SelectedStoryFilters {
  const nextFilters: SelectedStoryFilters = {};
  for (const key of Object.keys(filters)) {
    if (key === groupKey) {
      continue;
    }
    const values = filters[key];
    if (values) {
      nextFilters[key] = values;
    }
  }
  return nextFilters;
}

// ── Briefs variant renderer ──────────────────────────────────
function BriefsVariantRenderer({
  briefs,
  briefsReportDate,
  exploreBase,
  className,
  variant,
  activeCategory,
}: {
  briefs: import("./types").ExploreBriefCard[];
  briefsReportDate: string;
  exploreBase: string;
  className?: string;
  variant: BriefsVariant;
  activeCategory?: import("./types").ExploreCategory;
}) {
  // biome-ignore lint/nursery/noUnnecessaryConditions: exhaustive switch on union type
  switch (variant) {
    case "compact":
      return (
        <BriefsCompactSection
          briefs={briefs}
          briefsReportDate={briefsReportDate}
          className={className}
          exploreBase={exploreBase}
        />
      );
    case "compact5":
      return (
        <BriefsCompact5Section
          briefs={briefs}
          briefsReportDate={briefsReportDate}
          className={className}
          exploreBase={exploreBase}
        />
      );
    case "flat":
      return (
        <BriefsFlatSection
          briefs={briefs}
          briefsReportDate={briefsReportDate}
          className={className}
          exploreBase={exploreBase}
        />
      );
    case "sidebar":
      return (
        <BriefsSidebarSection
          briefs={briefs}
          briefsReportDate={briefsReportDate}
          className={className}
        />
      );
    default:
      return (
        <BriefsSection
          activeCategory={activeCategory}
          briefs={briefs}
          briefsReportDate={briefsReportDate}
          className={className}
          exploreBase={exploreBase}
        />
      );
  }
}

export function ExplorePage({
  activeCategory,
  briefsVariant = "default",
  categoryToolsRail,
  data,
  initialSearch,
}: ExplorePageProps) {
  const searchParams = useBrowserSearchParams({ initialSearch });
  const pathname = usePathname();
  const router = useRouter();
  const { exploreBase, reportType } = useExploreRoute();
  const layoutParam = searchParams.get("layout");
  const activeCategoryObj = activeCategory
    ? data.categories.find((c) => c.slug === activeCategory)
    : undefined;
  const activeCategoryName = activeCategoryObj?.displayName;
  const activeTags = useMemo(
    () => parseTagSlugs({ searchParams }),
    [searchParams]
  );

  const { pendingTagsRef, toggleTag: handleTagToggle } = useTagToggle({
    searchParams,
  });

  const [selectedFilters, setSelectedFilters] = useState<SelectedStoryFilters>(
    {}
  );

  // ── Lookup maps for ExploreFilterSheet ──────────────────────
  const tagDisplayNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of data.filterGroups) {
      if (group.key === "topic" || group.key === "product") {
        for (const opt of group.options) {
          map.set(opt.id, opt.label);
        }
      }
    }
    return map;
  }, [data.filterGroups]);

  const toolOptions = useMemo(() => {
    const toolGroup = data.filterGroups.find((g) => g.key === "product");
    return toolGroup?.options ?? [];
  }, [data.filterGroups]);

  const handleFilterChange = useCallback(
    ({
      groupKey,
      optionId,
      checked,
    }: {
      groupKey: string;
      optionId: string;
      checked: boolean;
    }) => {
      setSelectedFilters((prev) => {
        const current = prev[groupKey] ?? [];
        const nextForGroup = checked
          ? [...current, optionId]
          : current.filter((id) => id !== optionId);
        if (nextForGroup.length === 0) {
          return omitFilterGroup({ filters: prev, groupKey });
        }
        return { ...prev, [groupKey]: nextForGroup };
      });
    },
    []
  );

  const handleClearAllFilters = useCallback(() => {
    setSelectedFilters({});
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("tags");

    const nextQueryString = nextSearchParams.toString();
    router.push(nextQueryString ? `${pathname}?${nextQueryString}` : pathname);
  }, [pathname, router, searchParams]);

  // ── Unified filter sheet handlers ───────────────────────────
  const handleCategorySelect = useCallback(
    (slug: string | null) => {
      // Derive tags from pending state to avoid dropping a tag selected
      // in the same sheet interaction before React re-renders.
      const currentTags =
        pendingTagsRef.current ??
        searchParams.get("tags")?.split(",").filter(Boolean) ??
        [];

      const nextSearchParams = new URLSearchParams();
      if (currentTags.length > 0) {
        nextSearchParams.set("tags", currentTags.join(","));
      }
      const qs = nextSearchParams.toString();

      if (slug === null) {
        router.push(qs ? `${exploreBase}?${qs}` : exploreBase);
      } else {
        const categoryPath = `${exploreBase}/${slug}`;
        router.push(qs ? `${categoryPath}?${qs}` : categoryPath);
      }
    },
    [exploreBase, pendingTagsRef, router, searchParams]
  );

  // Single navigation that clears both category and tags at once
  const handleClearAll = useCallback(() => {
    pendingTagsRef.current = [];
    setSelectedFilters({});
    router.push(exploreBase);
  }, [exploreBase, pendingTagsRef, router]);

  const appliedFilters = useMemo(() => {
    const chips: Array<{
      groupKey: string;
      optionId: string;
      label: string;
    }> = [];
    for (const group of data.filterGroups) {
      const selectedIds = selectedFilters[group.key] ?? [];
      for (const selectedId of selectedIds) {
        const selectedOption = group.options.find(
          (option) => option.id === selectedId
        );
        if (!selectedOption) {
          continue;
        }
        chips.push({
          groupKey: group.key,
          optionId: selectedId,
          label: selectedOption.label,
        });
      }
    }
    return chips;
  }, [data.filterGroups, selectedFilters]);

  const activeTagSet = useMemo(
    () => (activeTags.length > 0 ? new Set(activeTags) : undefined),
    [activeTags]
  );

  const tagScopedStories = useMemo(() => {
    if (!activeTagSet) {
      return data.filterableStories;
    }

    return data.filterableStories.filter((story) =>
      story.allTagSlugs.some((slug) => activeTagSet.has(slug))
    );
  }, [activeTagSet, data.filterableStories]);

  const filteredStories = useMemo(
    () =>
      applyStoryFilters({
        stories: tagScopedStories,
        selectedFilters,
      }),
    [selectedFilters, tagScopedStories]
  );

  const allowedStoryIds = useMemo(
    () => new Set(filteredStories.map((story) => story.storyId)),
    [filteredStories]
  );

  const visibleTodayStories = useMemo(
    () =>
      data.todayStories.filter((story) => allowedStoryIds.has(story.storyId)),
    [allowedStoryIds, data.todayStories]
  );

  const visibleTopStories = useMemo(() => {
    // Deduplicate: never show a story that's already in Fresh Stories
    const freshIds = new Set(visibleTodayStories.map((s) => s.storyId));
    return data.topStories.filter(
      (story) =>
        allowedStoryIds.has(story.storyId) && !freshIds.has(story.storyId)
    );
  }, [allowedStoryIds, data.topStories, visibleTodayStories]);

  const hasActiveFilters = appliedFilters.length > 0 || activeTags.length > 0;

  const visibleBriefs = useMemo(() => {
    if (!(hasActiveFilters || activeCategory)) {
      return data.briefs;
    }
    const dropdownSlugs = new Set(Object.values(selectedFilters).flat());

    return data.briefs.filter((brief) => {
      if (activeCategory && !brief.allCategorySlugs.includes(activeCategory)) {
        return false;
      }
      if (
        activeTagSet &&
        !brief.allTagSlugs.some((slug) => activeTagSet.has(slug))
      ) {
        return false;
      }
      if (
        dropdownSlugs.size > 0 &&
        !brief.allTagSlugs.some((slug) => dropdownSlugs.has(slug))
      ) {
        return false;
      }
      return true;
    });
  }, [
    activeCategory,
    activeTagSet,
    data.briefs,
    hasActiveFilters,
    selectedFilters,
  ]);

  const visibleWorkflows = useMemo(
    () => data.workflows?.filter((w) => allowedStoryIds.has(w.id)) ?? [],
    [allowedStoryIds, data.workflows]
  );

  const visiblePrompts = useMemo(
    () => data.prompts?.filter((p) => allowedStoryIds.has(p.id)) ?? [],
    [allowedStoryIds, data.prompts]
  );

  // ── Sparse / empty results fallback ─────────────────────────
  // Count how many stories are actually visible on the page
  const totalVisibleStoryCount = hasActiveFilters
    ? filteredStories.length
    : visibleTodayStories.length + visibleTopStories.length;

  const SPARSE_THRESHOLD = 3;
  const isSparseResults =
    totalVisibleStoryCount > 0 && totalVisibleStoryCount <= SPARSE_THRESHOLD;
  const isEmptyResults = totalVisibleStoryCount === 0;

  // Build fallback list: exclude stories already shown on the page
  const fallbackStories = useMemo(() => {
    const shownIds = new Set([
      ...visibleTodayStories.map((s) => s.storyId),
      ...visibleTopStories.map((s) => s.storyId),
      ...(hasActiveFilters ? filteredStories.map((s) => s.storyId) : []),
    ]);
    return data.popularFallback
      .filter((s) => !shownIds.has(s.storyId))
      .slice(0, 5);
  }, [
    data.popularFallback,
    visibleTodayStories,
    visibleTopStories,
    filteredStories,
    hasActiveFilters,
  ]);

  const { isPending } = useNavigationTransition();

  return (
    <div
      data-explore-ready="true"
      style={{
        opacity: isPending ? 0.6 : 1,
        transition: "opacity 200ms ease-out",
        pointerEvents: isPending ? "none" : "auto",
      }}
    >
      <HeroSection />

      {/* Category pills – visible on all screen sizes */}
      <StickyCategoryPills
        activeCategory={activeCategory}
        activeTags={activeTags}
        categories={data.categories}
      />

      {/* Mobile: filter sheet for tags (categories duplicated inside for quick access) */}
      <ExploreFilterSheet
        activeCategory={activeCategory}
        activeTags={activeTags}
        categories={data.categories}
        filterableStories={data.filterableStories}
        onCategorySelect={handleCategorySelect}
        onClearAll={handleClearAll}
        onTagToggle={handleTagToggle}
        tagDisplayNames={tagDisplayNames}
        toolOptions={toolOptions}
      />

      {/* Layout switcher bar — only visible when ?layout is set */}
      {layoutParam && <LayoutSwitcher />}

      {/* Main layout: sidebar + content */}
      <div className="mx-auto grid w-full max-w-[1320px] grid-cols-1 gap-8 px-2 pb-12 sm:px-6 lg:grid-cols-[240px_1fr]">
        <ExploreSidebar
          activeTags={activeTags}
          categoryName={activeCategoryName}
          initialSearch={initialSearch}
          sidebar={data.sidebar}
          tagDisplayNames={tagDisplayNames}
          trendingTags={data.trendingTags}
        />

        <main
          className="flex min-w-0 flex-col gap-10"
          id="main-content"
          tabIndex={-1}
        >
          {categoryToolsRail}

          {/* ── F-series layout variants ───────────────────── */}
          {layoutParam === "f1" && (
            <Suspense fallback={null}>
              <ExploreF1Flagship
                allowMocks={allowExploreLayoutMocks}
                data={data}
                exploreBase={exploreBase}
                reportType={reportType}
              />
            </Suspense>
          )}
          {layoutParam === "f2" && (
            <Suspense fallback={null}>
              <ExploreF2Compact
                allowMocks={allowExploreLayoutMocks}
                data={data}
                exploreBase={exploreBase}
                reportType={reportType}
              />
            </Suspense>
          )}
          {layoutParam === "f3" && (
            <Suspense fallback={null}>
              <ExploreF3Editorial
                allowMocks={allowExploreLayoutMocks}
                data={data}
                exploreBase={exploreBase}
                reportType={reportType}
              />
            </Suspense>
          )}

          {/* ── Default layout ─────────────────────────────── */}
          {layoutParam !== "f1" &&
            layoutParam !== "f2" &&
            layoutParam !== "f3" && (
              <>
                {appliedFilters.length > 0 && (
                  <section className="rounded-xl border border-[var(--explore-border)] bg-[var(--explore-bg-card)] p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <p className="text-[0.8rem] text-[var(--explore-text-muted)] uppercase tracking-[0.05em]">
                        Filters active: {appliedFilters.length}
                      </p>
                      <button
                        className="border-none bg-transparent p-0 font-medium text-[0.8rem] text-[var(--explore-accent-teal)] underline"
                        onClick={handleClearAllFilters}
                        type="button"
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {appliedFilters.map((filter) => (
                        <button
                          className="rounded-full border border-[var(--explore-border)] bg-[var(--explore-bg)] px-3 py-1.5 text-[0.8rem] text-[var(--explore-text-secondary)] transition-colors hover:border-[var(--explore-text-muted)] hover:text-[var(--explore-text-primary)]"
                          key={`${filter.groupKey}-${filter.optionId}`}
                          onClick={() =>
                            handleFilterChange({
                              groupKey: filter.groupKey,
                              optionId: filter.optionId,
                              checked: false,
                            })
                          }
                          type="button"
                        >
                          {filter.label} x
                        </button>
                      ))}
                    </div>
                  </section>
                )}

                {hasActiveFilters && filteredStories.length > 0 && (
                  <section>
                    <p className="mb-4 text-[0.85rem] text-[var(--explore-text-muted)]">
                      {filteredStories.length} stor
                      {filteredStories.length === 1 ? "y" : "ies"} found
                    </p>
                    <div className="flex flex-col gap-4 px-1 lg:hidden">
                      {filteredStories.map((story, index) => (
                        <MobileStoryTextCard
                          exploreBase={exploreBase}
                          key={story.storyId}
                          showDivider={index < filteredStories.length - 1}
                          story={story}
                        />
                      ))}
                    </div>
                    <div className="hidden flex-col gap-3 lg:flex">
                      {filteredStories.map((story) => (
                        <StoryGridCard
                          exploreBase={exploreBase}
                          key={story.storyId}
                          story={story}
                        />
                      ))}
                    </div>
                  </section>
                )}

                {hasActiveFilters && visibleBriefs.length > 0 && (
                  <BriefsVariantRenderer
                    activeCategory={activeCategoryObj}
                    briefs={visibleBriefs}
                    briefsReportDate={data.briefsReportDate}
                    className="order-4"
                    exploreBase={exploreBase}
                    variant={briefsVariant}
                  />
                )}

                {!hasActiveFilters && (
                  <TodayStoriesSection
                    exploreBase={exploreBase}
                    stories={visibleTodayStories}
                  />
                )}
                {!hasActiveFilters && visibleBriefs.length > 0 && (
                  <BriefsVariantRenderer
                    activeCategory={activeCategoryObj}
                    briefs={visibleBriefs}
                    briefsReportDate={data.briefsReportDate}
                    className="order-2"
                    exploreBase={exploreBase}
                    variant={briefsVariant}
                  />
                )}
                {/* Skills: right after briefs for assembly variants (vertical on mobile) */}
                {!hasActiveFilters &&
                  briefsVariant !== "default" &&
                  (data.skills ?? []).length > 0 && (
                    <div className="[&_.scrollbar-none]:!flex-col [&_.scrollbar-none]:!overflow-visible [&_.scrollbar-none_>div]:!w-auto order-3">
                      <SkillsSpotlightSection
                        exploreBase={exploreBase}
                        skills={data.skills ?? []}
                      />
                    </div>
                  )}
                {!hasActiveFilters && visibleTopStories.length > 0 && (
                  <TopStoriesSection
                    className={
                      briefsVariant !== "default" ? "order-4" : "order-3"
                    }
                    exploreBase={exploreBase}
                    stories={visibleTopStories}
                  />
                )}
                {!hasActiveFilters && briefsVariant === "default" && (
                  <ErrorBoundary source="mobile_inline_newsletter_boundary">
                    <MobileInlineNewsletter className="order-4" />
                  </ErrorBoundary>
                )}
                {/* Web Reads hidden — not implemented properly yet */}
                {/* Community Pulse hidden — not implemented properly yet */}
                {!hasActiveFilters &&
                  briefsVariant === "default" &&
                  (data.skills ?? []).length > 0 && (
                    <SkillsSpotlightSection
                      className="order-7"
                      exploreBase={exploreBase}
                      skills={data.skills ?? []}
                    />
                  )}
                {!hasActiveFilters && visibleWorkflows.length > 0 && (
                  <WorkflowsSection
                    className="order-8"
                    workflows={visibleWorkflows}
                  />
                )}
                {!hasActiveFilters && visiblePrompts.length > 0 && (
                  <PromptsSection
                    className="order-9"
                    prompts={visiblePrompts}
                  />
                )}

                {(isSparseResults || isEmptyResults) &&
                  fallbackStories.length > 0 && (
                    <section className="order-1 flex flex-col gap-4">
                      <div className="flex items-center justify-between px-1">
                        <p
                          className="text-[0.8rem] text-[var(--explore-text-muted)] uppercase tracking-[0.05em]"
                          style={{ fontFamily: "var(--homepage-sans-stack)" }}
                        >
                          {activeCategoryName
                            ? `More in ${activeCategoryName}`
                            : "You might also like"}
                        </p>
                        {(activeTags ?? []).length > 0 && (
                          <button
                            className="border-none bg-transparent p-0 font-medium text-[0.8rem] text-[var(--explore-accent-teal)] underline"
                            onClick={handleClearAllFilters}
                            type="button"
                          >
                            Clear filters
                          </button>
                        )}
                      </div>
                      <div className="flex flex-col gap-4">
                        {fallbackStories.map((story) => (
                          <FeaturedStoryCard
                            exploreBase={exploreBase}
                            key={story.storyId}
                            story={story}
                          />
                        ))}
                      </div>
                    </section>
                  )}
              </>
            )}
        </main>
      </div>

      <ExploreFooter />
      <BottomNav />
    </div>
  );
}
